/**
 * Module drops — the in-run source the 51-module buildcraft space never had
 * (issue #9, docs/04 §5).
 *
 * Two things were broken and they are the same thing. `unlockedModuleIds` in
 * `meta/unlockEffects.ts` had no callers, so the Salvage track's five module
 * unlocks bought nothing; and modules had no acquisition path at all, so the
 * whole of §5 — twelve families, five slot kinds, the synergy surfacing of §7.1
 * — was reachable only when a rolled weapon happened to arrive carrying one.
 * A gate on a pool nobody can draw from is not a gate.
 *
 * Kills are the source. `Game.enemyCtx.onDeath` already fires once per kill and
 * already knows the rank, so nothing new had to be observed. The gate is
 * literally the one `Game.raiseAltar` puts on the altar's weapon pool, pointed
 * at modules:
 *
 *     unlockedModuleIds(progression, [...modules.values()])
 *
 * ------------------------------------------------------------------ two sources
 *
 * Crafting (#8) also makes modules now, so the two have to divide the space or
 * one of them is redundant:
 *
 *   - **Crafting is the reliable path.** It is deterministic, it costs carried
 *     material, it needs a rest node and a Salvage unlock, and both of its basic
 *     recipes output `kinetic` modules by design (`meta/crafting.ts`).
 *   - **Drops are the lucky path.** They cost nothing, they cannot be aimed, and
 *     they are weighted *away* from what crafting already reaches — kinetic
 *     rolls at `CRAFTED_WEIGHT`, a fifth of anything else.
 *
 * Kinetic is down-weighted rather than excluded, and that is load-bearing: a
 * fresh profile derives kinetic and nothing else (see `BASELINE_MODULE_FAMILIES`),
 * so a table that refused it outright would drop nothing whatsoever in run one —
 * the exact failure this issue is about, reintroduced one layer down. One weight
 * covers both ends of the ramp instead of two rules.
 *
 * ------------------------------------------------------------------- affinity
 *
 * What a thing dealt is what you salvage out of it. That is the same principle
 * docs/03 §6 applies to damage — what a body is *made of* decides what happens
 * to it — and it means the family you want is found by fighting the things that
 * use it, rather than by rolling more.
 */

import { DamageType } from '../entity/ecs';
import type { Rng } from '../core/rng';
import type { ModuleDef } from './types';

/** What a kill offers the table. Structural, so `EnemyDef` satisfies it. */
export interface DropSource {
  /** `EnemyDef.rank`: 0 base, 1 elite, 2 boss. */
  rank: number;
  /** `AttackDef.damageType` of its primary attack. */
  damageType: number;
}

/** Structural view of an enemy, so this file does not import `enemyData`. */
export interface EnemyLike {
  readonly rank: number;
  readonly attacks: readonly { readonly damageType: number }[];
}

/**
 * Chance a kill pays out, by rank.
 *
 * A grunt at 4% is roughly one module per two dozen kills, which is a *found*
 * thing rather than an income; an elite is the one you go out of your way for;
 * a boss is the guaranteed beat, matching how the sanctum altar is guaranteed
 * (#1 phase 4). Bosses are placed by worldgen and there are eight of them, so
 * "always" here is eight modules a run, not a faucet.
 */
export const DROP_CHANCE_BY_RANK: readonly number[] = [0.04, 0.18, 1];

/**
 * Families crafting can already produce, and how much a drop discounts them.
 * Keep this in step with `meta/crafting.ts`'s recipe outputs — the whole point
 * is that the two sources do not overlap.
 */
export const CRAFTABLE_FAMILIES: ReadonlySet<string> = new Set(['kinetic']);
const CRAFTED_WEIGHT = 0.2;

/** How much harder the matching family rolls off a thing that dealt that type. */
const AFFINITY_WEIGHT = 3;

/**
 * Damage type -> the module family it is salvaged into.
 *
 * `resonant` is deliberately absent: it is the only family with no damage type
 * of its own (it scales *other* things), so it is reached by the unweighted
 * roll rather than by affinity — which is the correct shape for a family whose
 * whole identity is that it has no element.
 */
export const AFFINITY_BY_DAMAGE_TYPE: Readonly<Record<number, string>> = {
  [DamageType.PHYSICAL]: 'kinetic',
  [DamageType.FIRE]: 'pyre',
  [DamageType.FROST]: 'frost',
  [DamageType.SHOCK]: 'galvanic',
  [DamageType.ACID]: 'corrosive',
  [DamageType.TOXIC]: 'fungal',
  [DamageType.VOID]: 'void',
  [DamageType.RADIANT]: 'radiant',
  [DamageType.BLOOD]: 'blood',
  [DamageType.TEMPORAL]: 'temporal',
  [DamageType.FUNGAL]: 'fungal',
  [DamageType.NANITE]: 'nanite',
};

/** Read a drop context off an enemy definition. */
export function dropSourceFor(def: EnemyLike): DropSource {
  return {
    rank: def.rank,
    // The primary attack is the thing's signature; a def with no attacks at all
    // still drops, it simply has no affinity to bias with.
    damageType: def.attacks[0]?.damageType ?? DamageType.PHYSICAL,
  };
}

/** Reused across every kill; this runs from the death path and must not allocate. */
const scratchIds: string[] = [];
const scratchWeights: number[] = [];

/**
 * Roll one kill's drop. Returns a module id, or null for the usual case of
 * nothing.
 *
 * `allowed` is the output of `unlockedModuleIds` and is the *only* gate — this
 * file never decides what a profile may hold, exactly as `raiseAltar` never
 * decides which weapon classes exist. `held` is what the run already carries;
 * a duplicate is not a reward, and `runModules` de-duplicates on push anyway, so
 * rolling one would read as a drop that silently did nothing.
 *
 * Anything unresolvable degrades to "no drop" rather than throwing. A death path
 * that can throw would turn a renamed module id into a crash on the frame the
 * player was already busy.
 */
export function rollModuleDrop(
  allowed: readonly string[],
  modules: ReadonlyMap<string, ModuleDef>,
  held: readonly string[],
  src: DropSource,
  rng: Rng,
): string | null {
  const chance = DROP_CHANCE_BY_RANK[src.rank] ?? DROP_CHANCE_BY_RANK[0];
  if (rng.float() >= chance) return null;

  const affinity = AFFINITY_BY_DAMAGE_TYPE[src.damageType];
  scratchIds.length = 0;
  scratchWeights.length = 0;
  let total = 0;
  for (const id of allowed) {
    const def = modules.get(id);
    if (!def) continue; // drift: an id the catalogue no longer has
    if (held.includes(id)) continue;
    let wgt = 1;
    if (CRAFTABLE_FAMILIES.has(def.family)) wgt *= CRAFTED_WEIGHT;
    if (def.family === affinity) wgt *= AFFINITY_WEIGHT;
    scratchIds.push(id);
    scratchWeights.push(wgt);
    total += wgt;
  }
  if (total <= 0) return null;

  let roll = rng.float() * total;
  for (let k = 0; k < scratchIds.length; k++) {
    roll -= scratchWeights[k];
    if (roll <= 0) return scratchIds[k];
  }
  // Float drift only; the last entry is as correct an answer as any.
  return scratchIds[scratchIds.length - 1];
}
